import { TFeature, TVesselInfo } from "../features/main-map/mapTypes";
import * as turf from '@turf/turf';

export const MapBoxToken = window.config.MAPBOX_ACCESS_TOKEN;

export const MapBoxGlobeMapStyles = {
    Msi_Globe: 'mapbox://styles/msi-webmaster/cm7c9eqvw004j01s284sw6713',
    Msi_Dusk: 'mapbox://styles/msi-webmaster/cm7dflc6y009y01ry6nq0f3z9',
    Msi_Day: 'mapbox://styles/msi-webmaster/cm7dfkbre00bg01r34xwuaz91',
    Msi_Dusk_Satellite: 'mapbox://styles/msi-webmaster/cm9bkcdhk004o01sb2i008kp1',
    Msi_Day_Satellite: 'mapbox://styles/msi-webmaster/cm9bkf9jp004g01s7eibmahs1',
}

export const styleKeyToUrl = (key: string) => MapBoxGlobeMapStyles[key as keyof typeof MapBoxGlobeMapStyles];
// Map style URL to key
export const urlToStyleKey = (url: string): string | undefined => {
    for (const [key, value] of Object.entries(MapBoxGlobeMapStyles)) {
        if (value === url) return key;
    }
    return undefined;
};

export const fixAntimeridian = (geojson: TVesselInfo) => {
    if (!geojson?.features) {
        return geojson;
    }
    const modifiedFeatures = geojson!.features.map(feature => {
        if (feature && feature.geometry && feature.geometry.type === 'LineString') {
            const { coordinates } = feature.geometry;

            if (!coordinates) {
                return feature;
            }

            let j = 0;
            const newCoordinates = coordinates.map(([lon, lat]: [number, number], _i: number, arr: [number, number][]) => {

                if (j === 0) {
                    j++;
                    return [lon, lat];
                }

                const prevLon = arr[j - 1]?.[0];
                const diff = lon - prevLon;

                let result: [number, number] = [lon, lat];

                if (diff > 180) {
                    j--;
                    result = [lon - 360, lat];
                } else if (diff < -180) {
                    j--;
                    result = [lon + 360, lat]
                } else {
                    j = _i;
                }
                j++;

                return result;
            });

            return {
                ...feature,
                geometry: {
                    ...feature.geometry,
                    coordinates: newCoordinates,
                },
            };
        }
        return feature;
    });

    return {
        ...geojson,
        features: modifiedFeatures,
    } as TVesselInfo;
};

const isTwoDimensionalArray = (arr: any) => {
    return Array.isArray(arr) && arr.some((element) => Array.isArray(element));
};

export const findCenterPoint = (geojson: any) => {

    const fixedGeojson = fixAntimeridian(geojson);

    if (!fixedGeojson || !fixedGeojson.features || fixedGeojson.features.length === 0) {
        return { latitude: 0, longitude: 0 }
    }
    const features = fixedGeojson.features;

    let totalLatitude = 0;
    let totalLongitude = 0;
    let count = 0;

    features.forEach((feature: any) => {
        // Skip features without geometry
        if (!feature || !feature.geometry || !feature.geometry.coordinates) return;

        const [longitude, latitude] = isTwoDimensionalArray(feature.geometry.coordinates) ? feature.geometry.coordinates[0] : feature.geometry.coordinates
        totalLatitude += latitude;
        totalLongitude += longitude;
        count += 1;
    });

    const centerLatitude = totalLatitude / count;
    const centerLongitude = totalLongitude / count;

    return { latitude: centerLatitude, longitude: centerLongitude };
};

export const createArrowIcon = (color: string, width: number = 64, height: number = 96) => {
    //const width = 64; // Width of the arrow base
    //const height = 96; // Height of the arrow
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = color;

    // Draw the arrow with a convex back
    ctx.beginPath();
    ctx.moveTo(width / 2, 0); // Tip of the arrow (top-center)
    ctx.lineTo(width, height * 0.8); // Bottom-right (close to the base)
    ctx.quadraticCurveTo(width / 2, height * 0.4, 0, height * 0.8); // Concave back
    ctx.closePath();
    ctx.fill();

    // Return the generated arrow image data
    return ctx.getImageData(0, 0, width, height);
};

export const createVesselIcon = (color: string, width: number = 64, height: number = 96) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = color;

    // Draw the vessel shape with a directional pointer
    ctx.beginPath();

    // Pointer tip (front of the vessel)
    ctx.moveTo(width / 2, 0); // Top-center

    // Right side of the vessel
    ctx.lineTo(width * 0.9, height * 0.4); // Right of the front body
    ctx.lineTo(width * 0.75, height * 0.9); // Bottom-right body

    // Bottom center
    ctx.lineTo(width / 2, height * 0.8); // Slight inward taper for vessel bottom

    // Left side of the vessel
    ctx.lineTo(width * 0.25, height * 0.9); // Bottom-left body
    ctx.lineTo(width * 0.1, height * 0.4); // Left of the front body

    ctx.closePath();
    ctx.fill();

    return ctx.getImageData(0, 0, width, height);
};

export const removeOutliers = (geojson: TVesselInfo, distanceThreshold: number): TVesselInfo => {
    const featuresWithOutliersRemoved: TFeature[] = [];

    for (const feature of geojson!.features) {
        if (feature && feature.geometry && feature.geometry.type === 'LineString') {
            const coordinates = feature.geometry.coordinates;
            const filteredCoordinates: number[][] = [];
            for (let i = 0; i < coordinates.length; i++) {
                let isOutlier = false;
                if (i > 0 && i < coordinates.length - 1) {
                    const prevPoint = turf.point(coordinates[i - 1]);
                    const currentPoint = turf.point(coordinates[i]);
                    const nextPoint = turf.point(coordinates[i + 1]);

                    const distToPrev = turf.distance(prevPoint, currentPoint);
                    const distToNext = turf.distance(currentPoint, nextPoint);

                    if (distToPrev > distanceThreshold || distToNext > distanceThreshold) {
                        isOutlier = true;
                    }
                }
                if (!isOutlier) {
                    filteredCoordinates.push(coordinates[i]);
                }
            }
            featuresWithOutliersRemoved.push({
                ...feature,
                geometry: {
                    type: 'LineString',
                    coordinates: filteredCoordinates,
                },
            });
        } else {
            // Handle other geometry types if needed (e.g., Point, Polygon)  -  For now, just add them unchanged
            featuresWithOutliersRemoved.push(feature);
        }
    }

    return {
        type: 'FeatureCollection',
        features: featuresWithOutliersRemoved,
    };
};